import { invoke, addPluginListener } from '@tauri-apps/api/core';

/**
 * Send toast notifications (brief auto-expiring OS window element) to your user.
 * Can also be used with the Notification Web API.
 *
 * @module
 */
/**
 * Predefined intervals for repeating notifications.
 */
var ScheduleEvery;
(function (ScheduleEvery) {
    ScheduleEvery["Year"] = "year";
    ScheduleEvery["Month"] = "month";
    ScheduleEvery["TwoWeeks"] = "twoWeeks";
    ScheduleEvery["Week"] = "week";
    ScheduleEvery["Day"] = "day";
    ScheduleEvery["Hour"] = "hour";
    ScheduleEvery["Minute"] = "minute";
    /**
     * Not supported on iOS.
     */
    ScheduleEvery["Second"] = "second";
})(ScheduleEvery || (ScheduleEvery = {}));
/**
 * Schedule configuration for notifications.
 */
class Schedule {
    /**
     * Creates a schedule to fire at a specific date and time.
     *
     * @param date - The date and time to fire the notification.
     * @param repeating - Whether to repeat the notification at the same time daily.
     * @param allowWhileIdle - On Android, allows notification to fire even when the device is in idle mode.
     * @returns A new Schedule instance.
     */
    static at(date, repeating = false, allowWhileIdle = false) {
        return {
            at: { date, repeating, allowWhileIdle },
            interval: undefined,
            every: undefined,
        };
    }
    /**
     * Creates a schedule using an interval configuration.
     *
     * @param interval - The interval configuration specifying when to fire.
     * @param allowWhileIdle - On Android, allows notification to fire even when the device is in idle mode.
     * @returns A new Schedule instance.
     */
    static interval(interval, allowWhileIdle = false) {
        return {
            at: undefined,
            interval: { interval, allowWhileIdle },
            every: undefined,
        };
    }
    /**
     * Creates a schedule to repeat at regular intervals.
     *
     * @param kind - The type of interval (year, month, week, day, hour, minute, second).
     * @param count - The number of intervals between notifications.
     * @param allowWhileIdle - On Android, allows notification to fire even when the device is in idle mode.
     * @returns A new Schedule instance.
     */
    static every(kind, count, allowWhileIdle = false) {
        return {
            at: undefined,
            interval: undefined,
            every: { interval: kind, count, allowWhileIdle },
        };
    }
}
/**
 * The importance level of a notification channel (Android).
 */
var Importance;
(function (Importance) {
    /** Does not show notifications. */
    Importance[Importance["None"] = 0] = "None";
    /** Shows notifications only in the notification shade, no sound, no visual interruption. */
    Importance[Importance["Min"] = 1] = "Min";
    /** Shows notifications everywhere, but is not intrusive. */
    Importance[Importance["Low"] = 2] = "Low";
    /** Shows notifications everywhere with sound. */
    Importance[Importance["Default"] = 3] = "Default";
    /** Shows notifications everywhere with sound and heads-up display. */
    Importance[Importance["High"] = 4] = "High";
})(Importance || (Importance = {}));
/**
 * The visibility of a notification on the lock screen (Android).
 */
var Visibility;
(function (Visibility) {
    /** Do not show any part of this notification on the lock screen. */
    Visibility[Visibility["Secret"] = -1] = "Secret";
    /** Show the notification, but hide sensitive content on the lock screen. */
    Visibility[Visibility["Private"] = 0] = "Private";
    /** Show the entire notification on the lock screen. */
    Visibility[Visibility["Public"] = 1] = "Public";
})(Visibility || (Visibility = {}));
/**
 * Checks if the permission to send notifications is granted.
 * @example
 * ```typescript
 * import { isPermissionGranted } from '@choochmeque/tauri-plugin-notifications-api';
 * const permissionGranted = await isPermissionGranted();
 * ```
 */
async function isPermissionGranted() {
    return await invoke("plugin:notifications|is_permission_granted");
}
/**
 * Requests the permission to send notifications.
 * @example
 * ```typescript
 * import { isPermissionGranted, requestPermission } from '@choochmeque/tauri-plugin-notifications-api';
 * let permissionGranted = await isPermissionGranted();
 * if (!permissionGranted) {
 *   const permission = await requestPermission();
 *   permissionGranted = permission === 'granted';
 * }
 * ```
 *
 * @returns A promise resolving to whether the user granted the permission or not.
 */
async function requestPermission() {
    return await invoke("plugin:notifications|request_permission");
}
/**
 * Registers the app for push notifications.
 *
 * `deviceToken` is the APNs token on iOS and the UnifiedPush endpoint URL on
 * Android/Linux. Pass a base64url VAPID public key to register against a Web
 * Push distributor; `p256dh` and `auth` are then set on the result.
 *
 * `embeddedGatewayUrl` enables the in-app distributor on Android, used when neither FCM
 * nor an installed distributor can deliver.
 *
 * @returns A promise resolving to the {@link PushRegistration}.
 */
async function registerForPushNotifications(vapid, provider = "auto", embeddedGatewayUrl) {
    return await invoke("plugin:notifications|register_for_push_notifications", {
        vapid,
        provider,
        embeddedGatewayUrl,
    });
}
/**
 * Unregisters the app from push notifications.
 *
 * This removes the device's push notification registration and stops
 * receiving remote pushes. On Linux it calls `Unregister` on the active
 * UnifiedPush distributor.
 *
 * @example
 * ```typescript
 * import { unregisterForPushNotifications } from '@choochmeque/tauri-plugin-notifications-api';
 * await unregisterForPushNotifications();
 * console.log('Unregistered from push notifications');
 * ```
 *
 * @returns A promise resolving when unregistration is complete.
 */
async function unregisterForPushNotifications() {
    await invoke("plugin:notifications|unregister_for_push_notifications");
}
/**
 * Lists currently running UnifiedPush distributors by D-Bus bus name
 * (e.g. `org.unifiedpush.Distributor.ntfy`).
 *
 * **Linux only, and only when the plugin was built with the
 * `push-notifications` Rust feature** (the Tauri commands are gated behind
 * `#[cfg(all(target_os = "linux", feature = "push-notifications"))]`).
 * Throws elsewhere because the command isn't registered.
 *
 * Returns an empty array when no UnifiedPush distributor is installed —
 * this is the signal to prompt the user to install one
 * (https://unifiedpush.org/users/distributors/).
 *
 * @example
 * ```typescript
 * import { listDistributors } from '@choochmeque/tauri-plugin-notifications-api';
 * const distributors = await listDistributors();
 * if (distributors.length === 0) {
 *   alert('Please install a UnifiedPush distributor');
 * }
 * ```
 *
 * @returns A promise resolving to the list of distributor bus names.
 * @platform linux
 */
async function listDistributors() {
    return await invoke("plugin:notifications|list_distributors");
}
/**
 * Pins the UnifiedPush distributor used for the next
 * {@link registerForPushNotifications} call.
 *
 * **Linux only, and only when the plugin was built with the
 * `push-notifications` Rust feature.** Throws elsewhere because the
 * underlying Tauri command isn't registered.
 *
 * **Must be called before {@link registerForPushNotifications}.** Calling
 * this after a successful register has no effect on the existing endpoint
 * — to switch distributors, unregister and register again.
 *
 * The selection lives only for the current process — it is **not persisted**
 * across launches. If the host app wants to remember the user's choice, it
 * must store the name itself and re-apply it via this function on startup.
 * If never called, the first distributor returned by {@link listDistributors}
 * is used.
 *
 * @example
 * ```typescript
 * import { setDistributor } from '@choochmeque/tauri-plugin-notifications-api';
 * await setDistributor('org.unifiedpush.Distributor.ntfy');
 * ```
 *
 * @param name The distributor bus name. Must be one of the values returned
 *             by {@link listDistributors}; otherwise rejects.
 * @platform linux
 */
async function setDistributor(name) {
    await invoke("plugin:notifications|set_distributor", { name });
}
/**
 * Sets the UnifiedPush client token used on subsequent
 * {@link registerForPushNotifications} calls.
 *
 * **Linux only, and only when the plugin was built with the
 * `push-notifications` Rust feature.** Throws elsewhere because the
 * underlying Tauri command isn't registered.
 *
 * **Must be called before {@link registerForPushNotifications}.** Calling
 * this after a successful register has no effect on the existing endpoint
 * — to get a different endpoint URL, unregister and register again.
 *
 * UnifiedPush distributors derive the endpoint URL from
 * `(connector_bus_name, client_token)`, so apps that want endpoint
 * stability across launches should persist a token (any non-empty string,
 * usually a UUID) and call this on startup before registering. If never
 * called, a fresh UUID is generated on each register call and the endpoint
 * URL will be unique per launch — the same model as FCM/APNs token
 * rotation, where the app pushes the new token to its backend each time.
 *
 * The token lives only for the current process — it is **not persisted**
 * across launches.
 *
 * @example
 * ```typescript
 * import { setToken, registerForPushNotifications } from '@choochmeque/tauri-plugin-notifications-api';
 * const storedToken = localStorage.getItem('up-client-token') ?? crypto.randomUUID();
 * localStorage.setItem('up-client-token', storedToken);
 * await setToken(storedToken);
 * const endpoint = await registerForPushNotifications();
 * ```
 *
 * @param token The client token. Must be non-empty.
 * @platform linux
 */
async function setToken(token) {
    await invoke("plugin:notifications|set_token", { token });
}
/**
 * Sends a notification to the user.
 * @example
 * ```typescript
 * import { isPermissionGranted, requestPermission, sendNotification } from '@choochmeque/tauri-plugin-notifications-api';
 * let permissionGranted = await isPermissionGranted();
 * if (!permissionGranted) {
 *   const permission = await requestPermission();
 *   permissionGranted = permission === 'granted';
 * }
 * if (permissionGranted) {
 *   sendNotification('Tauri is awesome!');
 *   sendNotification({ title: 'TAURI', body: 'Tauri is awesome!' });
 * }
 * ```
 */
async function sendNotification(options) {
    await invoke("plugin:notifications|notify", {
        options: typeof options === "string"
            ? {
                title: options,
            }
            : options,
    });
}
/**
 * Register actions that are performed when the user clicks on the notification.
 *
 * @example
 * ```typescript
 * import { registerActionTypes } from '@choochmeque/tauri-plugin-notifications-api';
 * await registerActionTypes([{
 *   id: 'tauri',
 *   actions: [{
 *     id: 'my-action',
 *     title: 'Settings'
 *   }]
 * }])
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function registerActionTypes(types) {
    await invoke("plugin:notifications|register_action_types", { types });
}
/**
 * Retrieves the list of pending notifications.
 *
 * @example
 * ```typescript
 * import { pending } from '@choochmeque/tauri-plugin-notifications-api';
 * const pendingNotifications = await pending();
 * ```
 *
 * @returns A promise resolving to the list of pending notifications.
 */
async function pending() {
    return await invoke("plugin:notifications|get_pending");
}
/**
 * Cancels the pending notifications with the given list of identifiers.
 *
 * @example
 * ```typescript
 * import { cancel } from '@choochmeque/tauri-plugin-notifications-api';
 * await cancel([-34234, 23432, 4311]);
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function cancel(notifications) {
    await invoke("plugin:notifications|cancel", { notifications });
}
/**
 * Cancels all pending notifications.
 *
 * @example
 * ```typescript
 * import { cancelAll } from '@choochmeque/tauri-plugin-notifications-api';
 * await cancelAll();
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function cancelAll() {
    await invoke("plugin:notifications|cancel_all");
}
/**
 * Retrieves the list of active notifications.
 *
 * @example
 * ```typescript
 * import { active } from '@choochmeque/tauri-plugin-notifications-api';
 * const activeNotifications = await active();
 * ```
 *
 * @returns A promise resolving to the list of active notifications.
 */
async function active() {
    return await invoke("plugin:notifications|get_active");
}
/**
 * Removes the active notifications with the given list of identifiers.
 *
 * @example
 * ```typescript
 * import { removeActive } from '@choochmeque/tauri-plugin-notifications-api';
 * await removeActive([{ id: 1 }, { id: 2, tag: 'news' }]);
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function removeActive(notifications) {
    await invoke("plugin:notifications|remove_active", { notifications });
}
/**
 * Removes all active notifications.
 *
 * @example
 * ```typescript
 * import { removeAllActive } from '@choochmeque/tauri-plugin-notifications-api';
 * await removeAllActive()
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function removeAllActive() {
    await invoke("plugin:notifications|remove_all");
}
/**
 * Creates a notification channel.
 *
 * @example
 * ```typescript
 * import { createChannel, Importance, Visibility } from '@choochmeque/tauri-plugin-notifications-api';
 * await createChannel({
 *   id: 'new-messages',
 *   name: 'New Messages',
 *   lights: true,
 *   vibration: true,
 *   importance: Importance.Default,
 *   visibility: Visibility.Private
 * });
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function createChannel(channel) {
    await invoke("plugin:notifications|create_channel", { channel });
}
/**
 * Removes the channel with the given identifier.
 *
 * @example
 * ```typescript
 * import { removeChannel } from '@choochmeque/tauri-plugin-notifications-api';
 * await removeChannel('new-messages');
 * ```
 *
 * @returns A promise indicating the success or failure of the operation.
 */
async function removeChannel(id) {
    await invoke("plugin:notifications|delete_channel", { id });
}
/**
 * Retrieves the list of notification channels.
 *
 * @example
 * ```typescript
 * import { channels } from '@choochmeque/tauri-plugin-notifications-api';
 * const notificationChannels = await channels();
 * ```
 *
 * @returns A promise resolving to the list of notification channels.
 */
async function channels() {
    return await invoke("plugin:notifications|list_channels");
}
/**
 * Registers a listener for incoming notifications.
 *
 * @example
 * ```typescript
 * import { onNotificationReceived } from '@choochmeque/tauri-plugin-notifications-api';
 * const unlisten = await onNotificationReceived((notification) => {
 *   console.log('Notification received:', notification);
 * });
 * // Later, to stop listening:
 * // unlisten();
 * ```
 *
 * @param cb - Callback function to handle received notifications.
 * @returns A promise resolving to a function that removes the listener.
 */
async function onNotificationReceived(cb) {
    return await addPluginListener("notifications", "notification", cb);
}
/**
 * Registers a listener for notification action events.
 *
 * @example
 * ```typescript
 * import { onAction } from '@choochmeque/tauri-plugin-notifications-api';
 * const unlisten = await onAction((notification) => {
 *   console.log('Action performed on notification:', notification);
 * });
 * // Later, to stop listening:
 * // unlisten();
 * ```
 *
 * @param cb - Callback function to handle notification action results.
 * @returns A promise resolving to a function that removes the listener.
 */
async function onAction(cb) {
    const listener = await addPluginListener("notifications", "actionPerformed", cb);
    try {
        await withActionListenerLock(async () => {
            if (actionListenerCount === 0) {
                await invoke("plugin:notifications|set_action_listener_active", {
                    active: true,
                });
            }
            actionListenerCount += 1;
        });
    }
    catch (error) {
        await listener.unregister();
        throw error;
    }
    let unregistered = false;
    return {
        unregister: async () => {
            if (unregistered)
                return;
            await withActionListenerLock(async () => {
                if (actionListenerCount > 1) {
                    actionListenerCount -= 1;
                    return;
                }
                // Keep the final listener until native deactivation succeeds.
                await invoke("plugin:notifications|set_action_listener_active", {
                    active: false,
                });
                actionListenerCount = 0;
            });
            unregistered = true;
            await listener.unregister();
        },
    };
}
let actionListenerCount = 0;
let actionListenerLock = Promise.resolve();
function withActionListenerLock(operation) {
    const result = actionListenerLock.then(operation, operation);
    actionListenerLock = result.then(() => undefined, () => undefined);
    return result;
}
/**
 * Registers a listener for notification click/tap events.
 * This fires when the user taps on a notification (both push and local).
 *
 * This function handles cold-start scenarios where the app is launched by
 * tapping a notification. Any pending notification click data is automatically
 * delivered when the listener is registered.
 *
 * @example
 * ```typescript
 * import { onNotificationClicked } from '@choochmeque/tauri-plugin-notifications-api';
 * const unlisten = await onNotificationClicked((data) => {
 *   console.log('Notification clicked, id:', data.id);
 *   console.log('Custom data:', data.data);
 * });
 * ```
 *
 * @param cb - Callback function to handle notification clicks.
 * @returns A promise resolving to a function that removes the listener.
 */
async function onNotificationClicked(cb) {
    const listener = await addPluginListener("notifications", "notificationClicked", cb);
    // Tell native side listener is active (triggers pending if any)
    await invoke("plugin:notifications|set_click_listener_active", {
        active: true,
    });
    // Return wrapped listener that notifies native side on unregister
    return {
        unregister: async () => {
            await invoke("plugin:notifications|set_click_listener_active", {
                active: false,
            });
            return listener.unregister();
        },
    };
}

export { Importance, Schedule, ScheduleEvery, Visibility, active, cancel, cancelAll, channels, createChannel, isPermissionGranted, listDistributors, onAction, onNotificationClicked, onNotificationReceived, pending, registerActionTypes, registerForPushNotifications, removeActive, removeAllActive, removeChannel, requestPermission, sendNotification, setDistributor, setToken, unregisterForPushNotifications };
